import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Eye, FlaskConical, Rocket, Clock, Ban, XCircle, Save, PlusCircle, ChevronDown, ListChecks, ImagePlus, X, Loader2,
  Megaphone, AlertTriangle, Zap, BellRing, Radio, CalendarClock, Wrench, CheckCircle2, Activity, RotateCcw, Sparkles,
  Check, Circle,
} from 'lucide-react';
import { uploadFile } from '../../composables/fetchAPI';
import NotificationPreviewCard from '../../components/Notifications/NotificationPreviewCard';
import {
  AdminNotification, AdminNotificationDraft, AudienceType, NotificationCategory, NotificationChannel,
  NotificationPriority, NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS, NOTIFICATION_CHANNELS,
  NOTIFICATION_TYPES, ADMIN_COMPOSABLE_CATEGORIES, PRODUCER_OWNED_CATEGORY_HINT, getTypePresentation,
  createAdminNotification, updateAdminNotification, getAdminNotification, previewAdminNotification,
  testAdminNotification, publishAdminNotification, scheduleAdminNotification, cancelAdminNotification,
  expireAdminNotification,
} from '../../composables/notificationAPI';

// image ถูกแยกเป็น field เฉพาะ (มีปุ่มอัปโหลด) จึงไม่อยู่ใน advanced JSON แล้ว
const ADVANCED_KEYS = ['actions', 'locale', 'translations', 'preferencePolicy', 'bypassQuietHours', 'expirationAt', 'collapseKey'];

// backend เช็ค: relative path ที่ขึ้นต้น / (ไม่ใช่ //) หรือ HTTPS URL ที่ไม่มี username/password (ดู notification-platform.md 4.1)
function isSafeContentUrl(url: string): boolean {
  if (url.startsWith('//')) return false;
  if (url.startsWith('/')) return true;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && !u.username && !u.password;
  } catch {
    return false;
  }
}
const firstTypeOf = (c: NotificationCategory) => NOTIFICATION_TYPES[c][0] ?? '';

// Preset = ทางลัดกรอกฟอร์ม: ตั้งหมวด/type ตามที่กด + ค่าการส่งมาตรฐาน (แก้ต่อเองได้ทุกช่อง)
interface Preset {
  category: NotificationCategory;
  type: string;
  description: string;
  icon: React.ComponentType<{ size?: number }>;
  color: string;
}
const PRESETS: Preset[] = [
  { category: 'ANNOUNCEMENT', type: 'ADMIN_ANNOUNCEMENT', description: 'ข่าวสารทั่วไปจากทีมงาน', icon: Megaphone, color: '#60A5FA' },
  { category: 'ANNOUNCEMENT', type: 'IMPORTANT_NOTICE', description: 'เรื่องสำคัญที่ต้องรู้', icon: AlertTriangle, color: '#FBBF24' },
  { category: 'ANNOUNCEMENT', type: 'BRAKING_NEWS', description: 'ข่าวตลาดที่ต้องรู้ทันที', icon: Zap, color: '#22C55E' },
  { category: 'ANNOUNCEMENT', type: 'LIVE_REMINDER', description: 'แจ้งก่อนเริ่มไลฟ์', icon: BellRing, color: '#A78BFA' },
  { category: 'ANNOUNCEMENT', type: 'LIVE_STARTED', description: 'แจ้งเมื่อไลฟ์เริ่มแล้ว', icon: Radio, color: '#FB923C' },
  { category: 'SYSTEM', type: 'MAINTENANCE_SCHEDULED', description: 'แจ้งล่วงหน้าก่อนปิดปรับปรุง', icon: CalendarClock, color: '#38BDF8' },
  { category: 'SYSTEM', type: 'MAINTENANCE_STARTED', description: 'เริ่มปิดปรับปรุงแล้ว', icon: Wrench, color: '#818CF8' },
  { category: 'SYSTEM', type: 'MAINTENANCE_COMPLETED', description: 'ปรับปรุงเสร็จแล้ว', icon: CheckCircle2, color: '#34D399' },
  { category: 'SYSTEM', type: 'SERVICE_DEGRADED', description: 'บริการช้าหรือผิดปกติ', icon: Activity, color: '#F87171' },
  { category: 'SYSTEM', type: 'SERVICE_RECOVERED', description: 'บริการกลับมาปกติ', icon: RotateCcw, color: '#2DD4BF' },
  { category: 'SYSTEM', type: 'PATCH_NOTES_PUBLISHED', description: 'มีอะไรใหม่ในเวอร์ชันล่าสุด', icon: Sparkles, color: '#E879F9' },
];
const PRESET_SETTINGS = {
  audienceType: 'ALL' as AudienceType,
  priority: 'LOW' as NotificationPriority,
  retentionDays: 3,
  channels: { IN_APP: true, WEB_PUSH: true, TELEGRAM: true } as Record<NotificationChannel, boolean>,
};

// แทน window.confirm ด้วย toast ยืนยัน (แบบเดียวกับที่ใช้ใน ArticlePage.tsx/RealtimePageV2.tsx)
// เพราะ window.confirm บล็อก UI แบบเนทีฟ หน้าตาไม่ตรงกับธีม และ mobile browser บางตัว UX แย่
function confirmToast(message: string, confirmLabel: string, onConfirm: () => void, danger = false) {
  toast((t) => (
    <div className="text-sm">
      <p className="mb-3">{message}</p>
      <div className="flex justify-end gap-2">
        <button
          onClick={() => toast.dismiss(t.id)}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold"
          style={{ backgroundColor: 'var(--bg-item)', color: 'var(--text-color)' }}
        >
          ยกเลิก
        </button>
        <button
          onClick={() => { toast.dismiss(t.id); onConfirm(); }}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold"
          style={danger
            ? { backgroundColor: 'var(--red-text-color-btn, #ef4444)', color: '#fff' }
            : { backgroundColor: 'var(--button-create-bg)', color: 'var(--button-text-color)' }}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  ), { duration: 20000 });
}

const emptyForm = () => ({
  category: 'ANNOUNCEMENT' as NotificationCategory,
  type: 'BRAKING_NEWS', // preset เริ่มต้น = ข่าวด่วน
  title: '',
  body: '',
  deepLink: '',
  image: '',
  audienceType: 'ALL' as AudienceType,
  audienceIds: '',
  channels: { ...PRESET_SETTINGS.channels },
  priority: PRESET_SETTINGS.priority,
  retentionDays: PRESET_SETTINGS.retentionDays,
  ackRequired: false,
  ackLabel: 'รับทราบ',
  advancedJson: '{}',
});
type FormState = ReturnType<typeof emptyForm>;

function idsFromText(text: string): string[] {
  return Array.from(new Set(text.split(/[\s,]+/).filter(Boolean)));
}

// แปลง ISO (UTC) จาก backend เป็นค่าที่ <input type="datetime-local"> ใช้ได้ (เวลาเครื่องนี้ ไม่มี timezone suffix)
function toDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function recordToForm(n: AdminNotification): FormState {
  return {
    category: n.category,
    type: n.type,
    title: n.title,
    body: n.body,
    deepLink: n.deepLink || '',
    image: (n as any).image || '',
    audienceType: n.audience.type,
    audienceIds: (n.audience.userIds || []).join(', '),
    channels: Object.fromEntries(NOTIFICATION_CHANNELS.map((c) => [c, n.channels.includes(c)])) as Record<NotificationChannel, boolean>,
    priority: n.priority || 'NORMAL',
    retentionDays: n.retentionDays || 3,
    ackRequired: !!n.acknowledgement?.required,
    ackLabel: n.acknowledgement?.label || 'รับทราบ',
    advancedJson: JSON.stringify(
      Object.fromEntries(ADVANCED_KEYS.filter((k) => (n as any)[k] !== undefined).map((k) => [k, (n as any)[k]])),
      null, 2,
    ),
  };
}

function buildPayload(form: FormState): AdminNotificationDraft {
  if (!/^[A-Z][A-Z0-9_]{1,79}$/.test(form.type)) throw new Error('Event type ต้องเป็นตัวพิมพ์ใหญ่/ตัวเลข/_ เท่านั้น');
  if (!form.title.trim()) throw new Error('กรุณาระบุหัวข้อ');
  if (!form.body.trim()) throw new Error('กรุณาระบุเนื้อหา');
  if (form.deepLink.trim() && !isSafeContentUrl(form.deepLink.trim())) {
    throw new Error('ลิงก์ต้องขึ้นต้นด้วย / (ไม่ใช่ //) หรือเป็น HTTPS URL ที่ไม่มี username/password');
  }
  if (form.image.trim() && !isSafeContentUrl(form.image.trim())) {
    throw new Error('รูปภาพต้องเป็น HTTPS URL ที่ไม่มี username/password');
  }

  let advanced: any = {};
  try {
    advanced = form.advancedJson.trim() ? JSON.parse(form.advancedJson) : {};
  } catch {
    throw new Error('Advanced JSON ไม่ถูกต้อง');
  }
  if (Array.isArray(advanced) || Object.keys(advanced).some((k) => !ADVANCED_KEYS.includes(k))) {
    throw new Error('Advanced JSON ใช้ได้เฉพาะ field: ' + ADVANCED_KEYS.join(', '));
  }

  const channels = NOTIFICATION_CHANNELS.filter((c) => form.channels[c]);
  if (!channels.length) throw new Error('เลือกอย่างน้อยหนึ่งช่องทาง');

  const audience: AdminNotificationDraft['audience'] = { type: form.audienceType };
  if (form.audienceType !== 'ALL') {
    const list = idsFromText(form.audienceIds);
    if (!list.length || list.length > 100) throw new Error('ระบุ User ID 1-100 รายการ');
    audience.userIds = list;
  }

  return {
    category: form.category,
    type: form.type,
    title: form.title.trim(),
    body: form.body.trim(),
    ...(form.deepLink.trim() ? { deepLink: form.deepLink.trim() } : {}),
    ...(form.image.trim() ? { image: form.image.trim() } : {}),
    audience,
    channels,
    priority: form.priority,
    retentionDays: Number(form.retentionDays) || 3,
    acknowledgement: { required: form.ackRequired, label: form.ackLabel.trim() || 'รับทราบ' },
    ...advanced,
  };
}

const inputCls = 'w-full rounded-lg px-3 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed';
// ขอบจาง ๆ ให้ช่องกรอกแยกจากพื้นการ์ด (การ์ดย้อมสีตาม preset แล้วช่องกรอกกลืนไปกับพื้น)
const inputStyle = { backgroundColor: 'var(--bg-item)', color: 'var(--text-color)', border: '1px solid var(--border-item-dark, rgba(255,255,255,0.1))' } as const;
const labelCls = 'text-xs font-semibold mb-1 block opacity-80';
// ปุ่ม action ทุกปุ่มใช้ style จางลง + cursor เปลี่ยนตอน disabled เพื่อให้เห็นชัดว่ากดไม่ได้ตอนนี้
const btnDisabledCls = 'disabled:opacity-40 disabled:cursor-not-allowed';

interface Props {
  selectedId: string;
  onSelectId: (id: string) => void;
  onGoToList: () => void;
}

const AdminNotificationCompose: React.FC<Props> = ({ selectedId, onSelectId, onGoToList }) => {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [record, setRecord] = useState<AdminNotification | null>(null);
  const [output, setOutput] = useState<string>('ผลการดำเนินการจะแสดงที่นี่');
  const [busy, setBusy] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // เผื่อเลือกไฟล์เดิมซ้ำ onChange จะได้ยิงอีก
    if (!file) return;
    setUploadingImage(true);
    try {
      const res = await uploadFile(file);
      if (!res?.url) throw new Error('อัปโหลดสำเร็จแต่ไม่ได้ URL กลับมา');
      set('image', res.url);
      toast.success('อัปโหลดรูปแล้ว');
    } catch (err: any) {
      toast.error(err.message || 'อัปโหลดรูปไม่สำเร็จ');
    } finally {
      setUploadingImage(false);
    }
  };

  useEffect(() => {
    if (!categoryOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) setCategoryOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [categoryOpen]);

  useEffect(() => {
    if (!selectedId) { setRecord(null); return; }
    (async () => {
      try {
        const n = await getAdminNotification(selectedId);
        setRecord(n);
        setForm(recordToForm(n));
        setScheduleAt(toDatetimeLocalValue(n.scheduledAt));
        setOutput(JSON.stringify(n, null, 2));
      } catch (err: any) {
        toast.error(err.message);
      }
    })();
  }, [selectedId]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const runAction = async (label: string, fn: () => Promise<any>, debugPayload?: unknown) => {
    setBusy(true);
    try {
      const res = await fn();
      setOutput(JSON.stringify(res, null, 2));
      toast.success(label + ' สำเร็จ');
      return res;
    } catch (err: any) {
      // แสดง payload ที่ส่งไปคู่กับ error เพราะ backend error message ทั่วไปไม่บอกว่า field ไหนผิด
      const parts = [`ผิดพลาด: ${err.message}${err.code ? ' (' + err.code + ')' : ''}`];
      if (debugPayload !== undefined) parts.push('--- payload ที่ส่งไป ---\n' + JSON.stringify(debugPayload, null, 2));
      setOutput(parts.join('\n\n'));
      toast.error(err.message || (label + ' ไม่สำเร็จ'));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = () => {
    let payload: AdminNotificationDraft;
    try { payload = buildPayload(form); } catch (err: any) { toast.error(err.message); return; }
    void runAction('สร้าง draft', async () => {
      const n = await createAdminNotification(payload);
      setRecord(n);
      onSelectId(n.id);
      return n;
    }, payload);
  };

  const handleUpdate = () => {
    if (!record) { toast.error('ยังไม่ได้เลือก draft'); return; }
    let payload: AdminNotificationDraft;
    try { payload = buildPayload(form); } catch (err: any) { toast.error(err.message); return; }
    void runAction('บันทึก draft', () => updateAdminNotification(record.id, payload).then((n) => { setRecord(n); return n; }), payload);
  };

  const handleClear = () => {
    setForm(emptyForm());
    setRecord(null);
    setScheduleAt('');
    onSelectId('');
    setOutput('เริ่ม draft ใหม่');
  };

  const guardId = () => {
    if (!record?.id) { toast.error('ยังไม่มี notification ที่เลือก'); return null; }
    return record.id;
  };

  const isEditable = !record || record.status === 'DRAFT';

  const pickPreset = (p: Preset) => setForm((f) => ({ ...f, ...PRESET_SETTINGS, category: p.category, type: p.type }));

  const audienceIdCount = idsFromText(form.audienceIds).length;
  const activeChannels = NOTIFICATION_CHANNELS.filter((c) => form.channels[c]);
  const checklist: { label: string; value: string; ok: boolean }[] = [
    { label: 'ประเภท', value: getTypePresentation(form.category, form.type).typeLabel, ok: !!form.type },
    { label: 'หัวข้อ', value: form.title.trim() || 'ยังไม่ได้ใส่', ok: !!form.title.trim() },
    { label: 'เนื้อหา', value: form.body.trim() ? `${form.body.trim().length} ตัวอักษร` : 'ยังไม่ได้ใส่', ok: !!form.body.trim() },
    { label: 'ผู้รับ', value: form.audienceType === 'ALL' ? 'ทุกคน' : `รายบุคคล (${audienceIdCount} คน)`, ok: form.audienceType === 'ALL' || audienceIdCount > 0 },
    { label: 'ช่องทาง', value: activeChannels.join(', ') || 'ยังไม่ได้เลือก', ok: activeChannels.length > 0 },
    { label: 'ความสำคัญ', value: form.priority, ok: true },
    { label: 'อายุ', value: `${form.retentionDays} วัน`, ok: form.retentionDays > 0 },
  ];

  const activePreset = PRESETS.find((p) => p.category === form.category && p.type === form.type);
  const formCardStyle: React.CSSProperties = activePreset
    ? {
      background: `linear-gradient(180deg, ${activePreset.color}2E 0%, ${activePreset.color}0F 45%, ${activePreset.color}0A 100%), var(--component-bg-color)`,
      boxShadow: `inset 0 0 0 1.5px ${activePreset.color}59`,
    }
    : { backgroundColor: 'var(--component-bg-color)' };

  return (
    <div className="grid lg:grid-cols-[1.3fr_1fr] xl:grid-cols-[220px_1.3fr_1fr] gap-5 items-start xl:items-stretch xl:h-full xl:grid-rows-[minmax(0,1fr)]">
      {/* แถบ preset: จอกว้างเป็นคอลัมน์ซ้ายเลื่อนขึ้นลงได้ จอแคบเป็นแถวเลื่อนซ้ายขวา */}
      <aside className="lg:col-span-2 xl:col-span-1 rounded-2xl p-3 xl:min-h-0 xl:flex xl:flex-col" style={{ backgroundColor: 'var(--component-bg-color)' }}>
        <h3 className="font-bold text-sm px-1 mb-0.5">Preset</h3>
        <p className="text-[11px] opacity-60 px-1 mb-2">{record ? 'ใช้ได้เฉพาะร่างใหม่ — กด "เริ่มร่างใหม่" ก่อน' : 'กดเพื่อกรอกฟอร์มให้อัตโนมัติ'}</p>
        <div className="scrollbar-hide flex xl:flex-col gap-2 overflow-x-auto xl:overflow-x-hidden xl:overflow-y-auto xl:flex-1 xl:min-h-0">
          {PRESETS.map((p) => {
            const Icon = p.icon;
            const selected = form.category === p.category && form.type === p.type;
            return (
              <button
                key={p.type}
                type="button"
                disabled={!!record} // เลือกรายการอยู่ (แก้ draft เดิม/ดูของที่ส่งแล้ว) ห้ามใช้ preset ทับค่าเดิม
                onClick={() => pickPreset(p)}
                aria-pressed={selected}
                className={'relative shrink-0 w-44 xl:w-auto flex items-start gap-2.5 text-left rounded-xl p-2.5 transition-colors ' + btnDisabledCls}
                style={{
                  border: `1.5px solid ${selected ? p.color : p.color + '40'}`,
                  backgroundColor: selected ? p.color + '1F' : p.color + '0D',
                }}
              >
                <span className="mt-0.5 shrink-0" style={{ color: p.color }}><Icon size={18} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-bold pr-4">{getTypePresentation(p.category, p.type).typeLabel}</span>
                  <span className="block text-[11px] opacity-60 leading-snug mt-0.5">{p.description}</span>
                </span>
                {selected && (
                  <span className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: p.color, color: '#0b1a12' }}>
                    <Check size={10} strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </aside>

      <div className="rounded-2xl p-5 scrollbar-hide xl:min-h-0 xl:overflow-y-auto transition-[background,box-shadow] duration-300" style={formCardStyle}>
        <h3 className="font-bold mb-4">1. สร้าง/แก้ไขเนื้อหาประกาศ {record && <span className="text-xs font-normal opacity-60">· {record.status}</span>}</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="relative" ref={categoryRef}>
            <label className={labelCls}>หมวด</label>
            <button
              type="button"
              disabled={!isEditable}
              onClick={() => setCategoryOpen((o) => !o)}
              className={inputCls + ' flex items-center justify-between text-left'}
              style={inputStyle}
            >
              <span>{NOTIFICATION_CATEGORY_LABELS[form.category]} · {form.category}</span>
              <ChevronDown size={14} className="opacity-60 shrink-0" />
            </button>
            {categoryOpen && (
              <div className="absolute z-20 mt-1 w-full rounded-lg shadow-xl overflow-hidden max-h-64 overflow-y-auto" style={{ backgroundColor: 'var(--component-bg-color)', border: '1px solid var(--bg-item)' }}>
                {NOTIFICATION_CATEGORIES.map((c) => {
                  const enabled = ADMIN_COMPOSABLE_CATEGORIES.includes(c);
                  const hint = PRODUCER_OWNED_CATEGORY_HINT[c] ?? 'เร็วๆนี้';
                  return (
                    <button
                      type="button"
                      key={c}
                      disabled={!enabled}
                      // เปลี่ยนหมวดแล้ว type เดิมไม่อยู่ในหมวดใหม่ จึงรีเซ็ตเป็น type แรกของหมวด
                      onClick={() => { setForm((f) => ({ ...f, category: c, type: firstTypeOf(c) })); setCategoryOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2"
                      style={enabled ? undefined : { opacity: 0.45, cursor: 'not-allowed' }}
                    >
                      <span>{NOTIFICATION_CATEGORY_LABELS[c]} · {c}</span>
                      {!enabled && <span className="text-[10px] font-bold shrink-0" style={{ color: 'var(--yellow-text-color)' }}>{hint}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className={labelCls}>Event type</label>
            <select disabled={!isEditable} className={inputCls} style={inputStyle} value={form.type} onChange={(e) => set('type', e.target.value)}>
              {/* draft เก่าอาจมี type ที่ไม่อยู่ในรายการหมวดปัจจุบัน (เช่น ARTICLE + ADMIN_ANNOUNCEMENT) — คงไว้ให้เห็น */}
              {!NOTIFICATION_TYPES[form.category].includes(form.type) && <option value={form.type}>{form.type}</option>}
              {NOTIFICATION_TYPES[form.category].map((t) => (
                <option key={t} value={t}>{getTypePresentation(form.category, t).typeLabel} · {t}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>หัวข้อ</label>
            <input disabled={!isEditable} maxLength={240} className={inputCls} style={inputStyle} value={form.title} onChange={(e) => set('title', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>เนื้อหา</label>
            <textarea disabled={!isEditable} maxLength={2000} rows={4} className={inputCls} style={inputStyle} value={form.body} onChange={(e) => set('body', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>ลิงก์เมื่อคลิก (ไม่บังคับ) — ใช้ path ภายในเว็บ เช่น /article/xxx</label>
            <input disabled={!isEditable} className={inputCls} style={inputStyle} value={form.deepLink} onChange={(e) => set('deepLink', e.target.value)} placeholder="/article/xxxxx" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>รูปภาพ (ไม่บังคับ)</label>
            <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={handleImageFile} />
            {form.image ? (
              <div className="flex items-center gap-3">
                <img src={form.image} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" style={{ backgroundColor: 'var(--bg-item)' }} />
                <div className="flex flex-col gap-2 min-w-0">
                  <span className="text-xs opacity-60 truncate">{form.image}</span>
                  <div className="flex gap-2">
                    <button type="button" disabled={!isEditable || uploadingImage} onClick={() => imageInputRef.current?.click()} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold" style={{ backgroundColor: 'var(--bg-item)' }}>
                      <ImagePlus size={13} /> เปลี่ยนรูป
                    </button>
                    <button type="button" disabled={!isEditable || uploadingImage} onClick={() => set('image', '')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold" style={{ color: 'var(--red-text-color)', backgroundColor: 'var(--bg-item)' }}>
                      <X size={13} /> ลบรูป
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                disabled={!isEditable || uploadingImage}
                onClick={() => imageInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: 'var(--bg-item)' }}
              >
                {uploadingImage ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                {uploadingImage ? 'กำลังอัปโหลด...' : 'เลือกรูป'}
              </button>
            )}
          </div>
          <div>
            <label className={labelCls}>ผู้รับ</label>
            <select disabled={!isEditable} className={inputCls} style={inputStyle} value={form.audienceType} onChange={(e) => set('audienceType', e.target.value as AudienceType)}>
              <option value="ALL">ทุกคน</option>
              <option value="INDIVIDUAL">รายบุคคล</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>ความสำคัญ</label>
            <select disabled={!isEditable} className={inputCls} style={inputStyle} value={form.priority} onChange={(e) => set('priority', e.target.value as NotificationPriority)}>
              {(['LOW', 'NORMAL', 'HIGH', 'CRITICAL'] as NotificationPriority[]).map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          {form.audienceType !== 'ALL' && (
            <div className="sm:col-span-2">
              <label className={labelCls}>User IDs (คั่นด้วย comma)</label>
              <input disabled={!isEditable} className={inputCls} style={inputStyle} value={form.audienceIds} onChange={(e) => set('audienceIds', e.target.value)} placeholder="ID_1, ID_2" />
            </div>
          )}
          <div>
            <label className={labelCls}>อายุ (วัน)</label>
            <input disabled={!isEditable} type="number" min={1} max={90} className={inputCls} style={inputStyle} value={form.retentionDays} onChange={(e) => set('retentionDays', Number(e.target.value))} />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs">
              <input disabled={!isEditable} type="checkbox" checked={form.ackRequired} onChange={(e) => set('ackRequired', e.target.checked)} />
              ต้องการให้กดรับทราบ
            </label>
          </div>
          {form.ackRequired && (
            <div className="sm:col-span-2">
              <label className={labelCls}>ข้อความปุ่มรับทราบ</label>
              <input disabled={!isEditable} maxLength={60} className={inputCls} style={inputStyle} value={form.ackLabel} onChange={(e) => set('ackLabel', e.target.value)} />
            </div>
          )}
          <div className="sm:col-span-2">
            <label className={labelCls}>ช่องทาง</label>
            <div className="flex gap-4">
              {NOTIFICATION_CHANNELS.map((c) => (
                <label key={c} className="flex items-center gap-1 text-xs">
                  <input disabled={!isEditable} type="checkbox" checked={form.channels[c]} onChange={(e) => set('channels', { ...form.channels, [c]: e.target.checked })} />
                  {c}
                </label>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <details>
              <summary className="text-xs font-semibold cursor-pointer opacity-80">ตัวเลือกขั้นสูง (JSON): actions, locale, translations, preferencePolicy, bypassQuietHours, expirationAt, collapseKey</summary>
              <textarea disabled={!isEditable} rows={5} className={inputCls + ' mt-2 font-mono text-xs'} style={inputStyle} value={form.advancedJson} onChange={(e) => set('advancedJson', e.target.value)} />
            </details>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            disabled={busy || !isEditable}
            title={!isEditable ? 'แก้ไขได้เฉพาะสถานะ DRAFT — กด "เริ่มร่างใหม่" เพื่อสร้างรายการใหม่' : undefined}
            onClick={handleCreate}
            className={'flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold ' + btnDisabledCls}
            style={{ backgroundColor: 'var(--button-create-bg)', color: 'var(--button-text-color)' }}
          >
            <PlusCircle size={14} /> สร้าง draft ใหม่
          </button>
          <button
            disabled={busy || !record || !isEditable}
            title={!record ? 'ต้องเลือกหรือสร้าง draft ก่อน' : !isEditable ? 'แก้ไขได้เฉพาะสถานะ DRAFT' : undefined}
            onClick={handleUpdate}
            className={'flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold ' + btnDisabledCls}
            style={{ backgroundColor: 'var(--button-edit-bg)', color: 'var(--button-text-color)' }}
          >
            <Save size={14} /> บันทึกทับ
          </button>
          <button disabled={busy} onClick={handleClear} className={'px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150 hover:opacity-85 active:scale-95 ' + btnDisabledCls} style={{ backgroundColor: 'var(--red-text-color-btn)', color: 'var(--button-text-color)' }}>
            เริ่มร่างใหม่
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:sticky lg:top-4 xl:static xl:min-h-0 xl:overflow-y-auto scrollbar-hide">
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--component-bg-color)' }}>
          <h3 className="font-bold mb-3">2. รายการที่เลือก</h3>
          {!record ? (
            <button onClick={onGoToList} className="w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold mb-3" style={{ backgroundColor: 'var(--bg-item)' }}>
              <ListChecks size={14} /> เลือกจากรายการประกาศ
            </button>
          ) : (
            <>
              <div className="text-xs opacity-70 mb-2 break-all">{record.id}</div>
              <div className="flex flex-wrap gap-2 mb-3">
                <button onClick={onGoToList} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ backgroundColor: 'var(--bg-item)' }}>
                  <ListChecks size={13} /> เปลี่ยน
                </button>
                <button onClick={handleClear} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 hover:opacity-85 active:scale-95" style={{ backgroundColor: 'var(--red-text-color-btn)', color: 'var(--button-text-color)' }}>
                  <X size={13} /> เลิกเลือก
                </button>
              </div>
            </>
          )}
          <div className="flex flex-wrap gap-2 mb-3">
            {/* พักฟีเจอร์ Preview ผู้รับไว้ก่อน
            <button disabled={busy || !record} onClick={() => runAction('Preview', () => previewAdminNotification(guardId()!))} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ backgroundColor: 'var(--bg-item)' }}>
              <Eye size={13} /> Preview ผู้รับ
            </button>
            */}
            <button
              disabled={busy || !record}
              title={!record ? 'ต้องเลือกหรือสร้าง draft ก่อน' : undefined}
              onClick={() => confirmToast('ส่ง test notification ให้บัญชี admin นี้?', 'ส่งทดสอบ', () => void runAction('ส่งทดสอบ', () => testAdminNotification(guardId()!)))}
              className={'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ' + btnDisabledCls}
              style={{ backgroundColor: 'var(--bg-item)' }}
            >
              <FlaskConical size={13} /> ส่งทดสอบให้ฉัน
            </button>
          </div>
          <button
            disabled={busy || !record || record.status !== 'DRAFT'}
            title={!record ? 'ต้องเลือกรายการดราฟก่อน ถึงจะกดเผยแพร่จริงได้' : record.status !== 'DRAFT' ? `เผยแพร่ได้เฉพาะสถานะ DRAFT (ตอนนี้: ${record.status})` : undefined}
            onClick={() => confirmToast('เผยแพร่จริงให้ audience ของรายการนี้?', 'เผยแพร่จริง', () => void runAction('เผยแพร่', () => publishAdminNotification(guardId()!).then((n) => { setRecord(n); return n; })))}
            className={'w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-bold ' + btnDisabledCls}
            style={{ backgroundColor: 'var(--button-create-bg)', color: 'var(--button-text-color)' }}
          >
            <Rocket size={14} /> เผยแพร่จริง
          </button>

          <hr className="my-4 opacity-20" />
          {/* พักฟีเจอร์ตั้งเวลาส่งไว้ก่อน
          <label className={labelCls}>ตั้งเวลา (เวลาเครื่องนี้)</label>
          <input type="datetime-local" disabled={busy || !record || record.status !== 'DRAFT'} className={inputCls} style={inputStyle} value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
          */}
          <div className="flex flex-wrap gap-2">
            {/* พักฟีเจอร์ตั้งเวลาส่งไว้ก่อน
            <button
              disabled={busy || !record || record.status !== 'DRAFT'}
              title={!record ? 'ต้องเลือกรายการดราฟก่อน' : record.status !== 'DRAFT' ? `ตั้งเวลาได้เฉพาะสถานะ DRAFT (ตอนนี้: ${record.status})` : undefined}
              onClick={() => {
                const at = new Date(scheduleAt);
                if (Number.isNaN(+at) || +at <= Date.now()) { toast.error('เลือกเวลาในอนาคต'); return; }
                if (!window.confirm('ตั้งเวลาส่ง ' + at.toLocaleString() + ' ?')) return;
                void runAction('ตั้งเวลา', () => scheduleAdminNotification(guardId()!, at.toISOString()).then((n) => { setRecord(n); setScheduleAt(toDatetimeLocalValue(n.scheduledAt)); return n; }));
              }}
              className={'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ' + btnDisabledCls}
              style={{ backgroundColor: 'var(--bg-item)' }}
            >
              <Clock size={13} /> ตั้งเวลาส่ง
            </button>
            */}
            {/* <button
              disabled={busy || !record || !['DRAFT', 'SCHEDULED'].includes(record.status)}
              title={!record ? 'ต้องเลือกรายการดราฟก่อน' : !['DRAFT', 'SCHEDULED'].includes(record.status) ? `ยกเลิกได้เฉพาะ DRAFT/SCHEDULED (ตอนนี้: ${record.status}) — ถ้าเผยแพร่แล้วให้ใช้ "ปิดประกาศ" แทน` : undefined}
              onClick={() => confirmToast('ยกเลิก draft / schedule นี้?', 'ยกเลิกรายการ', () => void runAction('ยกเลิก', () => cancelAdminNotification(guardId()!).then((n) => { setRecord(n); return n; })))}
              className={'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ' + btnDisabledCls}
              style={{ backgroundColor: 'var(--bg-item)' }}
            >
              <Ban size={13} /> ยกเลิก
            </button>
            <button
              disabled={busy || !record || record.status !== 'PUBLISHED'}
              title={!record ? 'ต้องเลือกรายการดราฟก่อน' : record.status !== 'PUBLISHED' ? `ปิดประกาศได้เฉพาะสถานะ PUBLISHED (ตอนนี้: ${record.status})` : undefined}
              onClick={() => confirmToast('ปิดประกาศนี้และหยุดการส่งที่ยังไม่เริ่ม?', 'ปิดประกาศ', () => void runAction('ปิดประกาศ', () => expireAdminNotification(guardId()!).then((n) => { setRecord(n); return n; })), true)}
              className={'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ' + btnDisabledCls}
              style={{ color: 'var(--red-text-color)' }}
            >
              <XCircle size={13} /> ปิดประกาศ
            </button> */}
          </div>
        </div>

        <div className="rounded-2xl p-5 shrink-0" style={{ backgroundColor: 'var(--component-bg-color)' }}>
          <h3 className="font-bold mb-3 text-sm">3. ผลลัพธ์</h3>
          <p className="text-xs opacity-60 mb-2">หน้าตาที่สมาชิกจะเห็นในแจ้งเตือน (ประมาณ — ยังไม่รวม preference/quiet hours ของแต่ละคน)</p>
          <NotificationPreviewCard
            item={{
              category: form.category,
              type: form.type,
              title: form.title.trim(),
              body: form.body.trim(),
              deepLink: form.deepLink.trim() || undefined,
              image: form.image.trim() || undefined,
              acknowledgement: { required: form.ackRequired, label: form.ackLabel },
            }}
          />
          <ul className="flex flex-col gap-1.5 mt-4">
            {checklist.map(({ label, value, ok }) => (
              <li key={label} className="flex items-start gap-2 text-sm">
                {ok
                  ? <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: 'var(--green-text-color)' }} />
                  : <Circle size={16} className="shrink-0 mt-0.5 opacity-40" />}
                <span className={ok ? '' : 'opacity-50'}>
                  <span className="opacity-70">{label}:</span> {value}
                </span>
              </li>
            ))}
          </ul>
          <details className="mt-3">
            <summary className="text-xs font-semibold cursor-pointer opacity-70">รายละเอียดเชิงเทคนิค (JSON)</summary>
            <pre className="text-[11px] whitespace-pre-wrap break-all max-h-[320px] overflow-auto rounded-lg p-3 mt-2" style={{ backgroundColor: 'var(--bg-item)' }}>{output}</pre>
          </details>
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationCompose;
